import { useState } from 'react';

import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import { BpkSingleSelectChipGroup } from '@skyscanner-internal/backpack-web/bpk-component-chip-group';
import BpkInfoBanner from '@skyscanner-internal/backpack-web/bpk-component-info-banner';
import BpkPrice from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  NearbyPickUpLocationsCardItem,
  NearbyPickUpLocationsProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/NearbyPickUpLocationsProps';

import STYLES from './NearbyPickUpLocations.module.scss';

const LocationCard = ({ card }: { card: NearbyPickUpLocationsCardItem }) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const ctaComponentName = `${rawComponentName}_cta`;

  return (
    <BpkCard
      href={card.url}
      className={STYLES.LocationCard}
      data-tracking-element-id={ctaComponentName}
      data-tracking-common-params={buildMBDParam(ctaComponentName)}
    >
      <div className={STYLES.LocationCard__header}>
        <div className={STYLES.LocationCard__info}>
          <BpkText textStyle={TEXT_STYLES.heading4} tagName="h3">
            {card.locationName}
          </BpkText>
          <BpkText
            textStyle={TEXT_STYLES.caption}
            tagName="p"
            className={STYLES.LocationCard__subtitle}
          >
            {card.subtitle}
          </BpkText>
        </div>
        <BpkPrice
          price={card.price}
          leadingText={card.priceLabel}
          trailingText={card.priceUnit}
          size="small"
          align="right"
        />
      </div>
    </BpkCard>
  );
};

const NearbyPickUpLocations = ({
  cardGroups,
  chips,
  defaultChipIndex = 0,
  description,
  disclaimer,
  title,
}: NearbyPickUpLocationsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const [selectedIndex, setSelectedIndex] = useState(defaultChipIndex);

  const chipItems = chips.map((chip) => ({
    text: chip.label,
  }));

  const activeCards = cardGroups[selectedIndex] ?? [];

  return (
    <div
      className={STYLES.NearbyPickUpLocations}
      data-testid="nearbyPickUpLocations"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <BpkSectionHeader title={title} description={description} />
      <BpkSingleSelectChipGroup
        chips={chipItems}
        selectedIndex={selectedIndex}
        onItemClick={(_item, _selected, index) => setSelectedIndex(index)}
        type="wrap"
        ariaLabel="Filter locations"
      />
      <div className={STYLES.CardGrid}>
        {activeCards.map((card) => (
          <LocationCard key={card.id} card={card} />
        ))}
      </div>
      {disclaimer && <BpkInfoBanner type="info" message={disclaimer} />}
    </div>
  );
};

const NearbyPickUpLocationsWithErrorBoundary = withErrorBoundary(
  NearbyPickUpLocations,
  'nearby-pick-up-locations-component',
);

export default NearbyPickUpLocationsWithErrorBoundary;
